import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { ProductProvider } from './context/ProductContext';
import { ThemeProvider } from './context/ThemeContext';
import { SiteProvider } from './context/SiteContext';
import { SiteTrackingProvider } from './context/SiteTrackingContext';
import { RedirectProvider, useRedirect } from './context/RedirectContext';
import SitePopupModal from './components/SitePopupModal/SitePopupModal';
import EngagementPopups from './components/EngagementPopups/EngagementPopups';

// Layout Components
import DocumentHead from './components/DocumentHead/DocumentHead';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import CartDrawer from './components/CartDrawer/CartDrawer';

// Page Components
import Home from './pages/Home';
import Shop from './components/Shop/Shop';
import ProductDetail from './components/ProductDetail/ProductDetail';
import Category from './components/Category/Category';
import { AuthProvider } from './context/AuthContext';
import SignIn from './components/SignIn/SignIn';
import Checkout from './components/Checkout/Checkout';
import Account from './components/Account/Account';
import { WishlistProvider } from './context/WishlistContext';
import Wishlist from './components/Wishlist/Wishlist';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import ShippingPolicy from './pages/ShippingPolicy';
import ReturnsPolicy from './pages/ReturnsPolicy';
import Contact from './pages/Contact';
import FAQs from './pages/FAQs';
import InfoPage from './pages/InfoPage';
import NotFound from './pages/NotFound';
import RedirectEmbed from './pages/RedirectEmbed';
import { startEmbedClickReporter } from './utils/embedClickBridge';

// When this build is shown inside the redirect offer iframe, report clicks to parent Tracing.
if (typeof window !== 'undefined') {
  startEmbedClickReporter();
}

function AppShell() {
  const { hideSiteChrome } = useRedirect();
  return (
    <div className="app">
      {!hideSiteChrome ? <Header /> : null}
      {!hideSiteChrome ? <CartDrawer /> : null}

      <main
        className={`main-content${hideSiteChrome ? ' main-content--redirect-embed' : ''}`}
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/category/:id" element={<Category />} />
          <Route path="/category/:id/:subcategory" element={<Category />} />
          <Route path="/sale" element={<Shop />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/account" element={<Account />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/shipping" element={<ShippingPolicy />} />
          <Route path="/returns" element={<ReturnsPolicy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faqs" element={<FAQs />} />
          <Route
            path="/rewards"
            element={
              <InfoPage title="Fashion Deals Rewards">
                <p>
                  Earn points on every purchase when you create a My Fashion Deals account. Redeem for member pricing, early drops, and free
                  shipping upgrades.
                </p>
              </InfoPage>
            }
          />
          <Route
            path="/stores"
            element={
              <InfoPage title="Stores">
                <p>
                  Visit our San Francisco flagship at 88 Howard Street, or shop online anytime. For store hours, use the contact details in
                  the footer.
                </p>
              </InfoPage>
            }
          />
          <Route
            path="/orders"
            element={
              <InfoPage title="Order history">
                <p>
                  Signed-in customers can track purchases from their account. Full order lookup and shipment history will appear here soon.
                </p>
              </InfoPage>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <InfoPage title="Reset password">
                <p>
                  Forgot your password? Contact customer support using the email or phone on our Contact page. Self-service reset is coming
                  soon.
                </p>
              </InfoPage>
            }
          />
          <Route
            path="/about"
            element={
              <InfoPage title="About myfashiondeals.online">
                <p>
                  myfashiondeals.online makes clothes and essentials for real weeks — women, men, activewear, home, and accessories. Styles can live in
                  multiple categories so you find them wherever you shop. Designed in California with fewer SKUs and better make. Contact us
                  anytime at hello@myfashiondeals.online or 1-888-847-8200.
                </p>
              </InfoPage>
            }
          />
          <Route
            path="/careers"
            element={
              <InfoPage title="Careers">
                <p>We’re hiring makers, designers, and operators. Career listings will be posted here — follow Deals List for openings.</p>
              </InfoPage>
            }
          />
          <Route
            path="/press"
            element={
              <InfoPage title="Press">
                <p>Media inquiries and lookbooks: reach us via the Contact page. Press kit downloads will live here soon.</p>
              </InfoPage>
            }
          />
          <Route
            path="/gift-cards"
            element={
              <InfoPage title="Gift cards">
                <p>myfashiondeals.online gift cards — digital and in-store — are coming soon. For now, share a favorite piece from the catalog.</p>
              </InfoPage>
            }
          />
          <Route
            path="/coupons"
            element={
              <InfoPage title="Offers">
                <p>Current promotions appear at checkout automatically when available. Join Deals List for early member offers.</p>
              </InfoPage>
            }
          />
          <Route path="/:slug" element={<RedirectEmbed />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      {!hideSiteChrome ? <Footer /> : null}
    </div>
  );
}

function App() {
  return (
    <SiteProvider>
      <Router>
        <SiteTrackingProvider>
          <RedirectProvider>
            <AuthProvider>
              <WishlistProvider>
                <CartProvider>
                  <ProductProvider>
                    <ThemeProvider>
                      <DocumentHead />
                      <SitePopupModal />
                      <EngagementPopups />
                      <AppShell />
                    </ThemeProvider>
                  </ProductProvider>
                </CartProvider>
              </WishlistProvider>
            </AuthProvider>
          </RedirectProvider>
        </SiteTrackingProvider>
      </Router>
    </SiteProvider>
  );
}

export default App;
